// Pure, coverage-testable Landscape Map logic — Story #4937 Stream 3b.
// jsdom has no canvas (vitest.config.ts's own note), so every piece of
// layout/scale/hit-test/binning math that the coverage floors (68/61/60/70)
// need to see lives HERE, in a DOM-free module — LandscapeMap.tsx stays a
// thin SVG render layer verified by the visual gate against screen1-map.png,
// not by vitest (this task's own Objective/Steps).
//
// REGION SHAPE IS DATA-DERIVED, NOT INVENTED (see computeRegionOutlines):
// the backend has no literal "white-space blob" geometry — a WOS-scored
// region is a cosine-similarity BAND (whitespace/handler.py's ANNULUS_BANDS),
// not a 2-D shape. Rather than guess a similarity-to-pixel-radius conversion
// (which the corpus-anchored PCA basis gives no honest way to calibrate —
// PCA does not preserve cosine distance), a region's outline is the bounding
// circle of that band's OWN cited boundary-record ids, wherever the /map
// layout actually placed them. A region whose ids never made it into the
// map's bounded neighbourhood sample (whitespace/handler.py's
// MAX_NEIGHBOURHOOD_RECORDS cap) renders no outline at all — an honest gap,
// same discipline as lib/whitespace.ts's "no invented narrative" regions.
import type { WhitespaceMapLayout, WhitespaceMapDates, WhitespaceRegion } from '../types/whitespace'

// ---------------------------------------------------------------------------
// Point model — the shape LandscapeMap.tsx renders
// ---------------------------------------------------------------------------

export type MapPointKind = 'project' | 'neighbourhood'

export interface MapPoint {
  id: string
  x: number
  y: number
  kind: MapPointKind
  /** YYYYMMDD, neighbourhood points only — undefined for a project point or
   * a neighbourhood point the backend could not resolve a date for. */
  date?: number
}

const PROJECT_PREFIX = 'project:'

/** Turn a raw {record_id: [x, y]} layout (+ optional per-id dates) into the
 * point list LandscapeMap.tsx renders. Pure mapping — no fetch, no DOM. */
export function layoutToPoints(layout: WhitespaceMapLayout, dates?: WhitespaceMapDates | null): MapPoint[] {
  return Object.entries(layout).map(([id, [x, y]]) => {
    const isProject = id.startsWith(PROJECT_PREFIX)
    return {
      id,
      x,
      y,
      kind: isProject ? 'project' : 'neighbourhood',
      date: isProject ? undefined : dates?.[id],
    }
  })
}

// ---------------------------------------------------------------------------
// Viewport scaling — data-space (x, y) <-> a fixed square SVG viewBox.
// Aspect-ratio-preserving: a distorted map would compress the very
// distances (whitespace) it exists to show honestly.
// ---------------------------------------------------------------------------

export interface Scale {
  viewBoxSize: number
  toScreen: (x: number, y: number) => [number, number]
  toData: (sx: number, sy: number) => [number, number]
  /** Data-space length of one screen-space unit — for converting a fixed
   * screen hit-radius or marker size into data space and back. */
  dataPerScreenUnit: number
}

const DEFAULT_VIEWBOX_SIZE = 100
const DEFAULT_PADDING_RATIO = 0.12
// A degenerate extent (0-1 points, or every point identical) still needs a
// span to scale against — an arbitrary-but-documented data-space unit,
// never a divide-by-zero.
const MIN_SPAN = 1e-6

/** Fit a linear, aspect-ratio-preserving scale over `points` into a square
 * viewBox of `viewBoxSize` x `viewBoxSize`, with `paddingRatio` of the
 * viewBox left as margin on every side. */
export function buildScale(
  points: { x: number; y: number }[],
  viewBoxSize = DEFAULT_VIEWBOX_SIZE,
  paddingRatio = DEFAULT_PADDING_RATIO,
): Scale {
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const minX = xs.length ? Math.min(...xs) : -1
  const maxX = xs.length ? Math.max(...xs) : 1
  const minY = ys.length ? Math.min(...ys) : -1
  const maxY = ys.length ? Math.max(...ys) : 1

  const spanX = Math.max(maxX - minX, MIN_SPAN)
  const spanY = Math.max(maxY - minY, MIN_SPAN)
  const span = Math.max(spanX, spanY)

  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2

  const drawable = viewBoxSize * (1 - 2 * paddingRatio)
  const scaleFactor = drawable / span

  const toScreen = (x: number, y: number): [number, number] => [
    viewBoxSize / 2 + (x - cx) * scaleFactor,
    // SVG y grows downward; data y should grow upward on the page (a
    // conventional scatter reads "up" as positive), so flip.
    viewBoxSize / 2 - (y - cy) * scaleFactor,
  ]
  const toData = (sx: number, sy: number): [number, number] => [
    (sx - viewBoxSize / 2) / scaleFactor + cx,
    -(sy - viewBoxSize / 2) / scaleFactor + cy,
  ]

  return { viewBoxSize, toScreen, toData, dataPerScreenUnit: 1 / scaleFactor }
}

// ---------------------------------------------------------------------------
// Density binning — corpus underlay as BINS, never a raw point dump (this
// task's own Steps). A grid, not per-point circles: readable even at the
// map's full corpus-neighbourhood size, and it is what makes "corpus
// density underlay" an honest description of what is drawn.
// ---------------------------------------------------------------------------

export interface DensityBin {
  /** Data-space center of this bin. */
  cx: number
  cy: number
  count: number
}

/** Bin `points` into a `gridSize` x `gridSize` grid over their own extent
 * (with the same padding convention as buildScale, so the underlay lines up
 * with points rendered through the same scale). Empty bins are omitted —
 * the render layer only ever draws bins that have real data in them. */
export function binPointsForDensity(
  points: { x: number; y: number }[],
  gridSize = 12,
  paddingRatio = DEFAULT_PADDING_RATIO,
): DensityBin[] {
  if (points.length === 0) return []

  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  const spanX = Math.max(maxX - minX, MIN_SPAN) * (1 + 2 * paddingRatio)
  const spanY = Math.max(maxY - minY, MIN_SPAN) * (1 + 2 * paddingRatio)
  const originX = (minX + maxX) / 2 - spanX / 2
  const originY = (minY + maxY) / 2 - spanY / 2
  const cellW = spanX / gridSize
  const cellH = spanY / gridSize

  const counts = new Map<string, number>()
  for (const p of points) {
    const col = Math.min(gridSize - 1, Math.max(0, Math.floor((p.x - originX) / cellW)))
    const row = Math.min(gridSize - 1, Math.max(0, Math.floor((p.y - originY) / cellH)))
    const key = `${col}:${row}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  const bins: DensityBin[] = []
  for (const [key, count] of counts) {
    const [colStr, rowStr] = key.split(':')
    const col = Number(colStr)
    const row = Number(rowStr)
    bins.push({
      cx: originX + (col + 0.5) * cellW,
      cy: originY + (row + 0.5) * cellH,
      count,
    })
  }
  return bins
}

// ---------------------------------------------------------------------------
// Region outlines — bounding circle of a region's OWN plotted boundary
// records (see module docstring for why this, not a fabricated blob).
// ---------------------------------------------------------------------------

export interface RegionOutline {
  regionKey: string
  cx: number
  cy: number
  radius: number
  wos_score: number
  pointCount: number
}

/** A stable key for one WOS band, matching OpportunityExplorer's own
 * `${band[0]}-${band[1]}` convention so a region selected on the map and one
 * selected in the Explorer are provably the same key. */
export function regionKeyFor(region: Pick<WhitespaceRegion, 'band'>): string {
  return `${region.band[0]}-${region.band[1]}`
}

// A region with only one (or zero-spread) plotted point still needs a
// visible, clickable outline rather than a zero-radius circle — this is the
// render floor, in data-space units (buildScale's viewBoxSize is 100 by
// default, so this reads as a small but real disc, never a fabricated
// "big enough to look right" guess).
const MIN_REGION_RADIUS = 1.5

/** Compute one bounding-circle outline per region that has at least one of
 * its own boundary-record ids present in `layout` — regions with none are
 * OMITTED (an honest gap: the map's bounded neighbourhood sample did not
 * happen to include that band's evidence), never drawn with an invented
 * position. Sorted by wos_score descending, so a caller drawing in list
 * order paints the highest-scored (and by the AC's own ranking, most
 * interesting) region last / on top. */
export function computeRegionOutlines(
  regions: WhitespaceRegion[],
  layout: WhitespaceMapLayout,
): RegionOutline[] {
  const outlines: RegionOutline[] = []

  for (const region of regions) {
    const ids = [...region.patent_record_ids, ...region.academic_record_ids]
    const points = ids.map((id) => layout[id]).filter((p): p is [number, number] => !!p)
    if (points.length === 0) continue

    const cx = points.reduce((sum, [x]) => sum + x, 0) / points.length
    const cy = points.reduce((sum, [, y]) => sum + y, 0) / points.length
    const radius = Math.max(
      MIN_REGION_RADIUS,
      ...points.map(([x, y]) => Math.hypot(x - cx, y - cy)),
    )

    outlines.push({
      regionKey: regionKeyFor(region),
      cx,
      cy,
      radius,
      wos_score: region.wos_score,
      pointCount: points.length,
    })
  }

  return outlines.sort((a, b) => a.wos_score - b.wos_score)
}

// ---------------------------------------------------------------------------
// Time slider — filter neighbourhood points by publication_date band.
// ---------------------------------------------------------------------------

/** [min, max] YYYYMMDD across every dated point, or null when nothing in
 * the layout carries a date (no slider is shown — an honest gap, never a
 * fabricated default range). */
export function dateRangeFromDates(dates: WhitespaceMapDates | null | undefined): [number, number] | null {
  const values = Object.values(dates ?? {})
  if (values.length === 0) return null
  return [Math.min(...values), Math.max(...values)]
}

/** Filter points by an inclusive [min, max] YYYYMMDD window. Project points
 * are ALWAYS kept (they are the inventor's own documents, not corpus prior
 * art the slider is meant to bound). A neighbourhood point with no date is
 * also always kept — hiding it would silently misrepresent "no date data"
 * as "outside the selected window", which is a different, false claim. */
export function filterPointsByDateRange(points: MapPoint[], range: [number, number]): MapPoint[] {
  const [lo, hi] = range
  return points.filter((p) => {
    if (p.kind === 'project') return true
    if (p.date === undefined) return true
    return p.date >= lo && p.date <= hi
  })
}

// ---------------------------------------------------------------------------
// Publication window — dual-thumb slider support (design §5.7: "an honest
// dual-thumb 'Publication window' with live year readouts and a records-
// per-year histogram").
// ---------------------------------------------------------------------------

export interface YearBucket {
  year: number
  count: number
}

/** One bucket per calendar year across `dateBounds`, counting only
 * neighbourhood points with a resolvable date (a project point carries no
 * publication_date at all — see MapPoint's own doc — so it never
 * contributes to this histogram). Years with zero records inside the bounds
 * still get a bucket (count 0) so the histogram's x-axis has no silent
 * gaps a viewer could misread as "no data collected" rather than "no
 * records that year". Returns [] when there is nothing to bucket (no dated
 * points, or no bounds) — an honest empty histogram, never a fabricated
 * single bar. */
export function recordsPerYear(points: MapPoint[], dateBounds: [number, number] | null): YearBucket[] {
  if (!dateBounds) return []
  const startYear = Math.floor(dateBounds[0] / 10000)
  const endYear = Math.floor(dateBounds[1] / 10000)
  if (!Number.isFinite(startYear) || !Number.isFinite(endYear) || startYear > endYear) return []

  const counts = new Map<number, number>()
  for (let y = startYear; y <= endYear; y++) counts.set(y, 0)
  for (const p of points) {
    if (p.kind !== 'neighbourhood' || p.date === undefined) continue
    const year = Math.floor(p.date / 10000)
    if (counts.has(year)) counts.set(year, (counts.get(year) ?? 0) + 1)
  }
  return [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([year, count]) => ({ year, count }))
}

/** Clamp a dual-thumb [lo, hi] YYYYMMDD range so lo never exceeds hi and
 * both stay within `bounds` — the one invariant both slider thumbs share,
 * kept here as pure logic so LandscapeMap.tsx's render layer never has to
 * reason about thumb-crossing itself. */
export function clampPublicationWindow(
  range: [number, number],
  bounds: [number, number],
): [number, number] {
  const lo = Math.min(Math.max(range[0], bounds[0]), bounds[1])
  const hi = Math.min(Math.max(range[1], bounds[0]), bounds[1])
  return lo <= hi ? [lo, hi] : [hi, hi]
}

// ---------------------------------------------------------------------------
// Hit-testing — screen click -> nearest point / containing region.
// ---------------------------------------------------------------------------

/** Id of the point within `hitRadiusData` of `dataXY`, preferring the
 * nearest when more than one qualifies — or null when nothing is close
 * enough (a click on empty canvas is not an accidental point selection). */
export function hitTestPoint(
  dataXY: [number, number],
  points: { id: string; x: number; y: number }[],
  hitRadiusData: number,
): string | null {
  const [px, py] = dataXY
  let best: { id: string; dist: number } | null = null
  for (const p of points) {
    const dist = Math.hypot(p.x - px, p.y - py)
    if (dist <= hitRadiusData && (!best || dist < best.dist)) {
      best = { id: p.id, dist }
    }
  }
  return best?.id ?? null
}

/** regionKey of the SMALLEST containing outline at `dataXY` — smallest, not
 * first-in-list, so a precise region nested inside a larger one's circle
 * stays independently clickable. Null when the click lands in open canvas,
 * outside every region. */
export function hitTestRegion(dataXY: [number, number], outlines: RegionOutline[]): string | null {
  const [px, py] = dataXY
  let best: { key: string; radius: number } | null = null
  for (const o of outlines) {
    const dist = Math.hypot(o.cx - px, o.cy - py)
    if (dist <= o.radius && (!best || o.radius < best.radius)) {
      best = { key: o.regionKey, radius: o.radius }
    }
  }
  return best?.key ?? null
}

/**
 * SVG points string for a five-pointed star centred at (cx, cy). Used for the
 * "Your idea" project marker on the Landscape Map so it matches the star shown
 * in the legend and help copy, rather than a plain dot indistinguishable from
 * the corpus markers (UX review Finding 5). `outer` is the tip radius, `inner`
 * the valley radius; the first point is at 12 o'clock.
 */
export function starPoints(cx: number, cy: number, outer: number, inner: number): string {
  const pts: string[] = []
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner
    const angle = (Math.PI / 5) * i - Math.PI / 2 // start at the top point
    pts.push(`${(cx + r * Math.cos(angle)).toFixed(3)},${(cy + r * Math.sin(angle)).toFixed(3)}`)
  }
  return pts.join(' ')
}
